/** Notices, on navigation, that the build this tab is running has been replaced by a deploy. */
import type { RouteLocationNormalized } from 'vue-router';

// Per tab rather than localStorage: a shared clock lets a freshly opened tab reset the window out from under the stale one.
export const NEXT_CHECK_KEY = 'manabua:version-next-check';
export const RELOAD_ATTEMPT_KEY = 'manabua:version-reload-attempted';

export const CHECK_INTERVAL_MS = 12 * 60 * 60 * 1000;
export const FAILED_CHECK_RETRY_MS = 5 * 60 * 1000;
export const FETCH_TIMEOUT_MS = 3000;

/** What a local build stamps when CI supplies no version; treated as unknown, so never reloaded on. */
export const DEVELOPMENT_VERSION = 'development';

export type VersionCheckResult =
  | 'no-current-version'
  | 'throttled'
  | 'unavailable'
  | 'up-to-date'
  | 'loop-guarded'
  | 'reload';

// Touching sessionStorage throws where the browser blocks site data, which would reject the guard and abort every navigation.
export interface SafeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export const safeStorage = (resolve: () => Storage): SafeStorage => ({
  getItem: (key) => {
    try {
      return resolve().getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      resolve().setItem(key, value);
    } catch {
      // Ignored - the check just runs more often than intended.
    }
  },
  removeItem: (key) => {
    try {
      resolve().removeItem(key);
    } catch {
      // Ignored, as above.
    }
  },
});

export interface VersionCheckDeps {
  currentVersion: string | undefined;
  versionUrl: string;
  reloadTo: string;
  now: () => number;
  storage: SafeStorage;
  fetch: typeof globalThis.fetch;
  reload: (url: string) => void;
}

const isUsableVersion = (version: string | undefined | null): version is string =>
  typeof version === 'string' && version.length > 0 && version !== DEVELOPMENT_VERSION;

const fetchDeployedVersion = async (deps: VersionCheckDeps): Promise<string | undefined> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await deps.fetch(deps.versionUrl, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) {
      return undefined;
    }

    // CloudFront rewrites 403/404 to 200 + index.html, so a failed upload arrives as a successful response full of HTML.
    const payload = (await response.json()) as { version?: unknown };
    return typeof payload?.version === 'string' && payload.version.length > 0 ? payload.version : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(timeout);
  }
};

const readDueTime = (storage: SafeStorage, now: number): number | null => {
  const raw = storage.getItem(NEXT_CHECK_KEY);

  // Number(null) and Number('') are both 0, which would read as a check that came due in 1970.
  if (raw === null || raw === '') {
    return null;
  }

  const parsed = Number(raw);

  // A due time beyond the interval means the clock has moved, and would otherwise suppress the check for the whole skew.
  if (!Number.isFinite(parsed) || parsed > now + CHECK_INTERVAL_MS) {
    return null;
  }

  return parsed;
};

export const checkForNewVersion = async (deps: VersionCheckDeps): Promise<VersionCheckResult> => {
  if (!isUsableVersion(deps.currentVersion)) {
    return 'no-current-version';
  }

  if (deps.storage.getItem(RELOAD_ATTEMPT_KEY) === deps.currentVersion) {
    // Cleared here rather than on boot, which would wipe the marker before the reload it was set for could be judged.
    deps.storage.removeItem(RELOAD_ATTEMPT_KEY);
  }

  const now = deps.now();
  const dueAt = readDueTime(deps.storage, now);

  if (dueAt === null) {
    // A tab that has just booted is current by definition, so start the clock rather than spend a request.
    deps.storage.setItem(NEXT_CHECK_KEY, String(now + CHECK_INTERVAL_MS));
    return 'throttled';
  }

  if (now < dueAt) {
    return 'throttled';
  }

  const deployedVersion = await fetchDeployedVersion(deps);

  if (deployedVersion === undefined) {
    deps.storage.setItem(NEXT_CHECK_KEY, String(now + FAILED_CHECK_RETRY_MS));
    return 'unavailable';
  }

  // Set before the reload decision so a reload cannot re-trigger the check on the way back in.
  deps.storage.setItem(NEXT_CHECK_KEY, String(now + CHECK_INTERVAL_MS));

  if (deployedVersion === deps.currentVersion) {
    return 'up-to-date';
  }

  if (deps.storage.getItem(RELOAD_ATTEMPT_KEY) === deployedVersion) {
    // Already reloaded for this exact version and came back running something else; reloading again would only spin.
    console.warn('Reloaded for version', deployedVersion, 'but still running', deps.currentVersion);
    return 'loop-guarded';
  }

  deps.storage.setItem(RELOAD_ATTEMPT_KEY, deployedVersion);
  deps.reload(deps.reloadTo);

  return 'reload';
};

// to.fullPath is relative to the router's history base, which previews set to /preview-x/; a bare fullPath lands on staging.
const historyBase = () => (import.meta.env.BASE_URL || '/').replace(/\/$/, '');

export const runVersionCheckForRoute = (to: RouteLocationNormalized): Promise<VersionCheckResult> =>
  checkForNewVersion({
    currentVersion: import.meta.env.VITE_APP_VERSION,
    versionUrl: `${import.meta.env.BASE_URL}version.json`,
    reloadTo: `${historyBase()}${to.fullPath}`,
    now: () => Date.now(),
    storage: safeStorage(() => window.sessionStorage),
    fetch: (...args) => globalThis.fetch(...args),
    reload: (url) => window.location.assign(url),
  });
